import type { GameId } from '@chessmate/protocol';
import { CheckersAdapter } from './checkers.js';
import { ChessAdapter } from './chess.js';
import type { GameAdapter } from './game-adapter.js';

/**
 * `Partial`, deliberately. `backgammon` was added to `GameId` while its engine
 * (`BackgammonAdapter`) does not — and honestly cannot — satisfy `GameAdapter`
 * (no FEN, nowhere for dice), so a full `Record` stopped compiling the moment
 * the id was ratified. The `Partial` + the runtime guard below is the interim
 * shape: a game id can exist in the protocol without being playable, and
 * `createGameAdapter('backgammon')` fails loudly instead of pretending. When
 * the board/UI wiring lands, backgammon gets its own routing (a dedicated
 * screen, not this chess-shaped factory) and this registry — or the factory —
 * is retired by whoever reads `docs/proposals/backgammon-wiring.md`.
 */
const ADAPTERS: Partial<Record<GameId, () => GameAdapter>> = {
  chess: () => new ChessAdapter(),
  checkers: () => new CheckersAdapter(),
};

export function createGameAdapter(gameId: GameId): GameAdapter {
  const make = ADAPTERS[gameId];
  if (!make) throw new Error(`Unknown game: ${gameId}`);
  return make();
}

export const GAME_IDS: readonly GameId[] = ['chess', 'checkers'];
// 'backgammon' is still missing ON PURPOSE, but for a narrower reason than the
// original plan: the board SHIPPED (board-3d #179) and the picker entry lives
// in the app (GamePicker), which routes backgammon to its own screen + store —
// never through GameAdapter. GAME_IDS answers "does createGameAdapter speak
// this id", i.e. what the API can host. It gains 'backgammon' only if/when an
// online backgammon adapter exists. See docs/proposals/backgammon-wiring.md.

/**
 * Type guard for "this id has a playable adapter today". The protocol union
 * can run ahead of playability (backgammon is ratified but unwired), so the
 * API and the picker must narrow through this, not through `GameId` alone.
 */
export function isPlayableGameId(id: GameId): id is 'chess' | 'checkers' {
  return (GAME_IDS as readonly string[]).includes(id);
}
